import { useNavigate } from 'react-router-dom'
import { Mic, BookOpen, CheckSquare, Crown, Trophy, Users, Gamepad2 } from 'lucide-react'
import { ThemedBackground } from '../components/ThemedBackground'
import { useTheme } from '../lib/theme-provider'

interface FeatureCardProps {
  icon: React.ReactNode
  title: string
  subtitle: string
  iconBgColor: string
  path?: string
  onClick?: () => void
}

interface FeatureCardProps {
  icon: React.ReactNode
  title: string
  subtitle: string
  iconBgColor: string
  path?: string
  onClick?: () => void
  showDevBadge?: boolean
}

const FeatureCard = ({ icon, title, subtitle, iconBgColor, path, onClick, showDevBadge }: FeatureCardProps) => {
  const navigate = useNavigate()
  const { theme } = useTheme()
  
  const handleClick = () => {
    if (onClick) {
      onClick()
    } else if (path) {
      navigate(path)
    }
  }
  
  return (
    <div
      onClick={handleClick}
      className="cursor-pointer transition-theme hover-scale-theme rounded-2xl p-4 relative"
      style={{
        backgroundColor: theme.colors['bg-card'],
        border: `2px solid ${theme.colors['border-secondary'] || '#5A4BFF'}35`,
        borderRadius: '16px',
      }}
    >
      {showDevBadge && (
        <div className="absolute top-2 right-2 px-1.5 py-0.5 rounded bg-amber-500 text-white text-[9px] font-bold">
          开发中
        </div>
      )}
      <div className="flex flex-col items-center justify-center h-full gap-3">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${iconBgColor}`}>
          {icon}
        </div>
        <div className="flex flex-col items-center justify-center">
          <h3 className="font-semibold text-[16px] mb-1" style={{ color: theme.colors['text-primary'] }}>{title}</h3>
          <p className="text-[12px]" style={{ color: theme.colors['text-secondary'] }}>{subtitle}</p>
        </div>
      </div>
    </div>
  )
}

export default function Home() {
  const features = [
    {
      icon: <Gamepad2 className="w-6 h-6 text-red-500" />,
      title: '线上游戏',
      subtitle: '多人在线对战',
      iconBgColor: 'bg-red-500/20',
      path: '/online-lobby',
      showDevBadge: true,
    },
    {
      icon: <Users className="w-6 h-6 text-green-400" />,
      title: '线下游戏',
      subtitle: '发身份、投票、笔记',
      iconBgColor: 'bg-green-400/20',
      path: '/offline-game',
    },

    {
      icon: <Mic className="w-6 h-6 text-amber-400" />,
      title: '语音主持',
      subtitle: '自动播报',
      iconBgColor: 'bg-amber-400/20',
      path: '/host',
    },
    {
      icon: <CheckSquare className="w-6 h-6 text-purple-400" />,
      title: '游戏笔记',
      subtitle: '组队投票记录',
      iconBgColor: 'bg-purple-400/20',
      path: '/vote',
    },
    {
      icon: <BookOpen className="w-6 h-6 text-cyan-400" />,
      title: '游戏指南',
      subtitle: '规则说明',
      iconBgColor: 'bg-cyan-400/20',
      path: '/guide',
    },
    {
      icon: <Trophy className="w-6 h-6 text-orange-500" />,
      title: '战绩统计',
      subtitle: '积分排行榜',
      iconBgColor: 'bg-orange-500/20',
      path: '/stats',
    },
  ]

  const { theme } = useTheme()
  
  return (
    <ThemedBackground pageName="home" className="px-3">
      <div className="text-center mb-3 pt-4">
        <div 
          className="w-14 h-14 mx-auto mb-3 rounded-xl flex items-center justify-center shadow-lg border-[3px]"
          style={{ 
            backgroundColor: theme.colors['bg-secondary'],
            borderColor: theme.colors['accent-gold'],
            boxShadow: theme.effects['shadow-card'],
          }}
        >
          <Crown className="w-10 h-10 text-amber-400" strokeWidth={1.5} />
        </div>
        <h1 className="text-[26px] font-bold mb-2 tracking-wide" style={{ color: theme.colors['text-primary'] }}>阿瓦隆</h1>
        <p className="text-[13px]" style={{ color: theme.colors['text-muted'] }}>游戏辅助工具</p>
      </div>

      <div className="flex flex-col gap-3 pb-2">
        <div className="grid grid-cols-2 gap-4">
          {features.map((feature) => (
            <FeatureCard
              key={feature.path}
              icon={feature.icon}
              title={feature.title}
              subtitle={feature.subtitle}
              iconBgColor={feature.iconBgColor}
              path={feature.path}
              showDevBadge={feature.showDevBadge}
            />
          ))}
        </div>
      </div>
    </ThemedBackground>
  )
}
